import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Menu, X, User, LogIn, LogOut, Home, Trophy, HelpCircle, MessageCircle, Mail, UserPlus, Shield } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import RegisterModal from './RegisterModal'
import { useAuth } from '../hooks/useAuth'
import faviconImg from '../assets/favicon.png'

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const { user, isAuthenticated, isAdmin, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = () => {
    logout()
  }

  const isInMyAccount = location.pathname === '/mi-cuenta'

  const handleNavigation = (sectionId) => {
    // Si no estamos en la página principal, navegar primero
    if (location.pathname !== '/') {
      navigate('/')
      // Esperar un poco para que la página cargue antes de hacer scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    } else {
      // Si ya estamos en la página principal, hacer scroll directamente
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/80 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-2"
          >
            <div>
              <img src={faviconImg} alt="Logo" className="h-10 w-10" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Membresía</h1>
              <p className="text-xs text-gray-300">ParaisoTour</p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => handleNavigation('inicio')} className="text-white hover:text-primary-400 transition-colors">
              Inicio
            </button>
            <button onClick={() => handleNavigation('membresias')} className="text-white hover:text-primary-400 transition-colors">
              Membresías
            </button>
            <button onClick={() => handleNavigation('como-funciona')} className="text-white hover:text-primary-400 transition-colors">
              Cómo Funciona
            </button>
            <button onClick={() => handleNavigation('testimonios')} className="text-white hover:text-primary-400 transition-colors">
              Testimonios
            </button>
            <button onClick={() => handleNavigation('contacto')} className="text-white hover:text-primary-400 transition-colors">
              Contacto
            </button>
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/mi-cuenta')}
                  className={`flex items-center space-x-2 transition-colors ${
                    isInMyAccount 
                      ? 'text-primary-400 bg-primary-400/20 px-3 py-2 rounded-lg' 
                      : 'text-white hover:text-primary-400'
                  }`}
                >
                  <User className="h-4 w-4" />
                  <span>{isInMyAccount ? 'Mi Cuenta (Activo)' : 'Mi Cuenta'}</span>
                </motion.button>
                {isAdmin && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/admin')}
                    className={`flex items-center space-x-2 transition-colors ${
                      location.pathname === '/admin'
                        ? 'text-yellow-400 bg-yellow-400/20 px-3 py-2 rounded-lg' 
                        : 'text-white hover:text-yellow-400'
                    }`}
                  >
                    <Shield className="h-4 w-4" />
                    <span>{location.pathname === '/admin' ? 'Panel Admin (Activo)' : 'Panel Admin'}</span>
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-white hover:text-red-400 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Cerrar Sesión</span>
                </motion.button>
              </>
            ) : (
              <>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/login')}
                  className="flex items-center space-x-2 text-white hover:text-primary-400 transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Iniciar Sesión</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowRegisterModal(true)}
                  className="btn-primary flex items-center space-x-2"
                >
                  <User className="h-4 w-4" />
                  <span>Registrarse</span>
                </motion.button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white hover:text-primary-400 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{ height: isOpen ? 'auto' : 0 }}
          className="md:hidden overflow-hidden bg-gradient-to-b from-black/95 to-gray-900/95 backdrop-blur-lg border-t border-gray-700/50"
        >
          <div className="px-4 py-6 space-y-1">
            {/* Navigation Links */}
            <motion.button 
              onClick={() => handleNavigation('inicio')} 
              className="flex items-center w-full px-4 py-3 text-left text-white hover:text-primary-400 hover:bg-white/5 rounded-lg transition-all duration-200 group"
              whileHover={{ x: 4 }}
            >
              <Home className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary-400 transition-colors" />
              <span className="font-medium">Inicio</span>
            </motion.button>
            
            <motion.button 
              onClick={() => handleNavigation('membresias')} 
              className="flex items-center w-full px-4 py-3 text-left text-white hover:text-primary-400 hover:bg-white/5 rounded-lg transition-all duration-200 group"
              whileHover={{ x: 4 }}
            >
              <Trophy className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary-400 transition-colors" />
              <span className="font-medium">Membresías</span>
            </motion.button>
            
            <motion.button 
              onClick={() => handleNavigation('como-funciona')} 
              className="flex items-center w-full px-4 py-3 text-left text-white hover:text-primary-400 hover:bg-white/5 rounded-lg transition-all duration-200 group"
              whileHover={{ x: 4 }}
            >
              <HelpCircle className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary-400 transition-colors" />
              <span className="font-medium">Cómo Funciona</span>
            </motion.button>
            
            <motion.button 
              onClick={() => handleNavigation('testimonios')} 
              className="flex items-center w-full px-4 py-3 text-left text-white hover:text-primary-400 hover:bg-white/5 rounded-lg transition-all duration-200 group"
              whileHover={{ x: 4 }}
            >
              <MessageCircle className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary-400 transition-colors" />
              <span className="font-medium">Testimonios</span>
            </motion.button>
            
            <motion.button 
              onClick={() => handleNavigation('contacto')} 
              className="flex items-center w-full px-4 py-3 text-left text-white hover:text-primary-400 hover:bg-white/5 rounded-lg transition-all duration-200 group"
              whileHover={{ x: 4 }}
            >
              <Mail className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary-400 transition-colors" />
              <span className="font-medium">Contacto</span>
            </motion.button>
            
            {/* Divider */}
            <div className="my-4 border-t border-gray-700/50"></div>
            
            {/* Auth Section */}
            <div className="space-y-2">
              {isAuthenticated ? (
                <>
                  <motion.button 
                    onClick={() => navigate('/mi-cuenta')}
                    className={`flex items-center w-full px-4 py-3 rounded-lg transition-all duration-200 group ${
                      isInMyAccount 
                        ? 'text-primary-400 bg-primary-400/20 border border-primary-400/30' 
                        : 'text-white hover:text-primary-400 hover:bg-white/5'
                    }`}
                    whileHover={{ x: 4 }}
                  >
                    <User className="h-5 w-5 mr-3 transition-colors" />
                    <span className="font-medium">
                      {isInMyAccount ? 'Mi Cuenta (Activo)' : 'Mi Cuenta'}
                    </span>
                  </motion.button>
                  
                  {isAdmin && (
                    <motion.button 
                      onClick={() => navigate('/admin')}
                      className={`flex items-center w-full px-4 py-3 rounded-lg transition-all duration-200 group ${
                        location.pathname === '/admin'
                          ? 'text-yellow-400 bg-yellow-400/20 border border-yellow-400/30' 
                          : 'text-white hover:text-yellow-400 hover:bg-white/5'
                      }`}
                      whileHover={{ x: 4 }}
                    >
                      <Shield className="h-5 w-5 mr-3 transition-colors" />
                      <span className="font-medium">
                        {location.pathname === '/admin' ? 'Panel Admin (Activo)' : 'Panel Admin'}
                      </span>
                    </motion.button>
                  )}
                  
                  <motion.button 
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-white hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all duration-200 group"
                    whileHover={{ x: 4 }}
                  >
                    <LogOut className="h-5 w-5 mr-3 text-gray-400 group-hover:text-red-400 transition-colors" />
                    <span className="font-medium">Cerrar Sesión</span>
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button 
                    onClick={() => navigate('/login')}
                    className="flex items-center w-full px-4 py-3 text-white hover:text-primary-400 hover:bg-white/5 rounded-lg transition-all duration-200 group"
                    whileHover={{ x: 4 }}
                  >
                    <LogIn className="h-5 w-5 mr-3 text-gray-400 group-hover:text-primary-400 transition-colors" />
                    <span className="font-medium">Iniciar Sesión</span>
                  </motion.button>
                  
                  <motion.button 
                    onClick={() => setShowRegisterModal(true)}
                    className="w-full mt-3 btn-primary flex items-center justify-center space-x-2 py-3 rounded-lg font-medium"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <UserPlus className="h-5 w-5" />
                    <span>Registrarse</span>
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Register Modal */}
      <RegisterModal 
        isOpen={showRegisterModal} 
        onClose={() => setShowRegisterModal(false)} 
      />
    </motion.header>
  )
}

export default Header